import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import { AirOffersStatus, BookingAppState, BookingLocationCodes } from '@fcom/common/interfaces/booking';
import { FinnairOrder } from '@fcom/dapi/api/models';
import { BookingHeaderSummary, MultivariateTestId, TestVariant } from '@fcom/common/interfaces';
import { Breadcrumb, PhasesAria } from '@fcom/common-booking/interfaces/booking-header-interfaces';
import {
  ElementActions,
  ElementTypes,
  GaContext,
  GlobalBookingTripDates,
  isNavigationEvent,
  MediaQueryService,
  NavigationMenuService,
  ScrollService,
  Step,
  TARGET_HEIGHT_ELEMENT,
  TripType,
} from '@fcom/common';
import { LanguageService } from '@fcom/ui-translate';
import { JoinService, LoginDialogComponent, LoginWidgetContext } from '@fcom/common/login';
import { PageMetaService } from '@fcom/common/services';
import { getBookingStepFromUrl } from '@fcom/common/utils/booking-common.utils';
import { ConfigService, NativeBridgeService } from '@fcom/core';
import { LoginStatus, ProfileType } from '@fcom/core-api/login';
import { loginStatus, profileName, profileType } from '@fcom/core/selectors';
import { GlobalBookingFlight } from '@fcom/common/store/store.interface';
import { isPresent, TzDate, unsubscribe } from '@fcom/core/utils';
import { orderData } from '@fcom/common-booking/store';
import { BookingSummaryService } from '@fcom/common-booking';
import { GtmService } from '@fcom/common/gtm';
import { finShare } from '@fcom/rx';
import { globalBookingSelections } from '@fcom/common/store';
import { getTripTypeFromBookingLocationCodes } from '@fcom/common-booking/utils/common-booking.utils';
import { BOOKING_STEPS, BOOKING_STEPS_ARRAY, ERROR_STEPS } from '@fcom/common/config/booking-config';
import { LanguageSelectorContext } from '@fcom/common/components/language-selector/language-selector.component';
import { ExpanderStatus } from '@fcom/booking-widget/interfaces';
import { MultivariateTestService } from '@fcom/common/multivariate-test/services/multivariate-test.service';

import { AppConstants } from '../../app-constants';
import { BookingService } from '../../services';
import { BookingGtmService } from '../../services/booking-gtm.service';
import { boundsStatus, cartData, offersStatus, selectMultiCityStatus } from '../../store/selectors';
import { currentServices } from '../../store/selectors/booking-cart-state.selector';
import { BookingActions } from '../../store/actions';

const SHOW_BUTTON_STATES = [LoginStatus.LOGGED_IN, LoginStatus.NOT_LOGGED_IN, LoginStatus.ERROR];

@Component({
  selector: 'fin-booking-routing',
  templateUrl: './booking-routing.component.html',
  styleUrls: ['./booking-routing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingRoutingComponent implements OnInit, OnDestroy {
  readonly TARGET_HEIGHT_ELEMENT = TARGET_HEIGHT_ELEMENT;
  readonly LANGUAGE_SELECTOR_CONTEXT = LanguageSelectorContext.BOOKING;
  @ViewChild('loginDialog', { static: true }) loginDialog: LoginDialogComponent;

  public readonly C: typeof AppConstants = AppConstants;
  public readonly BOOKING_STEPS = BOOKING_STEPS;
  public readonly LoginWidgetContext = LoginWidgetContext;
  readonly TRACKING_CONTEXT = GaContext.BOOKING_FLOW;
  currentStep$: Observable<Step>;
  isFirstStep$: Observable<boolean>;
  basketOpen$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  hasBasket$: Observable<boolean>;
  enableModifySearch$: Observable<boolean>;
  isErrorRoute$: Observable<boolean>;
  showBasket$: Observable<boolean>;
  isBookingFrontPage$: Observable<boolean>;
  homeLink$: Observable<string>;
  breadcrumbs$: Observable<Breadcrumb[]> = of([]);
  phasesAria$: Observable<PhasesAria>;
  isNavigationMenuOpen$: Observable<boolean>;
  loginStatus$: Observable<LoginStatus>;
  showHeaderBack$: Observable<boolean>;
  showHeaderLogin$: Observable<boolean>;
  profileName$: Observable<string>;
  profileType$: Observable<ProfileType>;
  unpaidServices$: Observable<BookingSummaryService[]>;
  bookingSummary$: Observable<BookingHeaderSummary>;
  orderSummary$: Observable<{
    locations: BookingLocationCodes[];
    dates: GlobalBookingTripDates;
    paxAmount: number;
  }>;
  isMobile$: Observable<boolean>;
  isTablet$: Observable<boolean>;
  isTabletDown$: Observable<boolean>;
  boundsStatus$: Observable<AirOffersStatus>;
  shouldEnableGlobalBookingWidget$: Observable<boolean>;
  enableNewStepper$: Observable<boolean>;
  private subscriptions: Subscription = new Subscription();
  stepper$: Observable<Breadcrumb[]>;
  isInsideNativeWebView: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platform: unknown,
    private bookingService: BookingService,
    private navigationMenuService: NavigationMenuService,
    private languageService: LanguageService,
    private router: Router,
    private scrollService: ScrollService,
    private renderer: Renderer2,
    private store$: Store<BookingAppState>,
    private nativeBridgeService: NativeBridgeService,
    private pageMetaService: PageMetaService,
    private gtmService: GtmService,
    private configService: ConfigService,
    private mediaQueryService: MediaQueryService,
    private multivariateTestService: MultivariateTestService,
    public joinService: JoinService,
    _bookingGtmService: BookingGtmService // to get service into background,
  ) {
    this.currentStep$ = this.bookingService.step;
    this.isFirstStep$ = this.bookingService.isFirstStep();
    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');
    this.isTablet$ = this.mediaQueryService.isBreakpoint$('tablet');
    this.isTabletDown$ = this.mediaQueryService.isBreakpoint$('tablet_down');

    this.orderSummary$ = this.store$.pipe(
      orderData(),
      filter((order) => Boolean(order?.bounds.length > 0)),
      map((order) => {
        return {
          dates: {
            departureDate: TzDate.of(order.bounds[0].departure.dateTime)?.toLocalDate(),
            returnDate:
              order.bounds.length > 1
                ? TzDate.of(order.bounds[order.bounds.length - 1].arrival.dateTime)?.toLocalDate()
                : null,
          },
          locations: order.bounds.map((bound) => ({
            originLocationCode: bound.departure.locationCode,
            destinationLocationCode: bound.arrival.locationCode,
          })),
          paxAmount: order.passengers.length,
        };
      })
    );

    this.bookingSummary$ = combineLatest([
      this.store$.pipe(boundsStatus(), startWith(null)),
      this.store$.pipe(offersStatus(), startWith(null)),
      this.store$.pipe(select(selectMultiCityStatus), startWith(null)),
    ]).pipe(
      withLatestFrom(this.store$.pipe(globalBookingSelections()), this.orderSummary$.pipe(startWith(null))),
      filter(
        ([_, globalBooking, orderSummary]) =>
          globalBooking.flights.some((flight) => isPresent(flight.origin) && isPresent(flight.destination)) ||
          isPresent(orderSummary)
      ),
      map(([_, { flights, paxAmount }, orderSummary]) => {
        const bookingLocations: BookingLocationCodes[] = flights
          .filter((flight) => isPresent(flight.origin) && isPresent(flight.destination))
          .map((flight: GlobalBookingFlight) => ({
            originLocationCode: flight.origin.locationCode,
            destinationLocationCode: flight.destination.locationCode,
          }));

        const bookingPaxAmount = paxAmount.adults + paxAmount.children + paxAmount.c15s + paxAmount.infants;
        const dates: GlobalBookingTripDates = { departureDate: flights[0]?.departureDate };

        if (flights.length > 1) {
          dates.returnDate = flights[flights.length - 1].departureDate;
        }

        return {
          tripType: bookingLocations.length
            ? getTripTypeFromBookingLocationCodes(bookingLocations)
            : getTripTypeFromBookingLocationCodes(orderSummary.locations),
          locations: bookingLocations.length ? bookingLocations : orderSummary.locations,
          paxAmount: bookingLocations.length ? bookingPaxAmount : orderSummary.paxAmount,
          dates: bookingLocations.length ? dates : orderSummary.dates,
        };
      })
    );

    this.homeLink$ = this.languageService.lang.pipe(map((lang) => `/${lang}`));
    this.isNavigationMenuOpen$ = this.navigationMenuService.isNavigationMenuOpen$;

    this.onNavigationSetBookingServiceStepFromUrl();

    this.showBasket$ = combineLatest([this.bookingService.step, this.isMobile$]).pipe(
      map(
        ([step, isMobile]) =>
          step.phase === BOOKING_STEPS.PAXDETAILS.phase ||
          step.phase === BOOKING_STEPS.ANCILLARIES.phase ||
          step.phase === BOOKING_STEPS.SEATS.phase ||
          (step.phase === BOOKING_STEPS.FLIGHT_SELECTION.phase && isMobile)
      ),
      distinctUntilChanged(),
      finShare()
    );

    this.isBookingFrontPage$ = this.bookingService.step.pipe(map((step) => step.phase === BOOKING_STEPS.ENTRY.phase));
  }

  ngOnInit(): void {
    this.loginStatus$ = this.store$.pipe(loginStatus());
    this.profileName$ = this.store$.pipe(profileName());
    this.profileType$ = this.store$.pipe(profileType());

    this.enableNewStepper$ = this.configService.cfg.enableNewStepper
      ? this.multivariateTestService.getTestVariant(MultivariateTestId.NEW_BOOKING_FLOW_STEPPER).pipe(
          map((variant) => variant.variant === TestVariant.B),
          finShare()
        )
      : of(false);
    this.isInsideNativeWebView = this.nativeBridgeService.isInsideNativeWebView;

    this.scrollToTopOnStepChange();

    this.bindBasketHandling();

    this.initButtonVisibilities();

    const showLinks$ = this.store$.pipe(
      orderData(),
      startWith(undefined as FinnairOrder),
      map((o) => !o),
      distinctUntilChanged(),
      finShare()
    );

    this.breadcrumbs$ = combineLatest([
      this.currentStep$,
      this.languageService.translate('phases.title'),
      showLinks$,
      this.store$.pipe(cartData(), startWith(undefined)),
      this.enableNewStepper$,
    ]).pipe(
      map(([currentStage, breadcrumbs, showLinks, cartData, enableNewStepper]) => {
        const hideAncillaries =
          cartData && !cartData.controlData.sellAncillariesOutbound && !cartData.controlData.sellAncillariesInbound;
        const hideSeatMap = !enableNewStepper;
        return this.formatBreadcrumbs(
          currentStage,
          breadcrumbs,
          showLinks,
          hideAncillaries,
          hideSeatMap,
          enableNewStepper
        );
      })
    );

    this.stepper$ = combineLatest([
      this.currentStep$,
      this.languageService.translate('stepper'),
      showLinks$,
      this.store$.pipe(cartData(), startWith(undefined)),
      this.enableNewStepper$,
    ]).pipe(
      map(([currentStage, breadcrumbs, showLinks, cartData, enableNewStepper]) => {
        const hideAncillaries =
          cartData && !cartData.controlData.sellAncillariesOutbound && !cartData.controlData.sellAncillariesInbound;
        const hasSeatBenefit = this.bookingService.cartHasCorporateSeatBenefit(cartData);
        const hideSeatMap = !enableNewStepper || hasSeatBenefit;

        return this.formatBreadcrumbs(
          currentStage,
          breadcrumbs,
          showLinks,
          hideAncillaries,
          hideSeatMap,
          enableNewStepper
        ).filter((b) => b.id !== BOOKING_STEPS.CHECKOUT.id);
      })
    );

    this.phasesAria$ = this.languageService.translate('phases.aria');

    this.showHeaderBack$ = combineLatest([this.bookingService.step, showLinks$]).pipe(
      map(
        ([currentStep, showLinks]) =>
          showLinks &&
          BOOKING_STEPS.ENTRY.id !== currentStep.id &&
          (this.isInsideNativeWebView ? true : BOOKING_STEPS.FLIGHT_SELECTION.id !== currentStep.id) &&
          BOOKING_STEPS.PURCHASE_SUCCESS.id !== currentStep.id
      ),
      distinctUntilChanged()
    );

    this.showHeaderLogin$ = combineLatest([this.bookingService.step, this.loginStatus$]).pipe(
      map(([step, status]) => {
        if (this.isInsideNativeWebView) {
          return false;
        }

        if (step.id > BOOKING_STEPS.PAXDETAILS.id) {
          return false;
        }

        return SHOW_BUTTON_STATES.indexOf(status) !== -1;
      })
    );

    this.subscriptions.add(
      this.currentStep$
        .pipe(
          distinctUntilChanged((prevStep, curStep) => prevStep.path === curStep.path),
          switchMap((step) => this.languageService.translate(`phases.title.${step.translationKey}`))
        )
        .subscribe((title) => {
          this.pageMetaService.setTitle(`${title} | Finnair`);
        })
    );

    if (isPlatformBrowser(this.platform)) {
      this.subscriptions.add(
        this.nativeBridgeService.backButtonPressed.subscribe(() => {
          this.bookingService.prevStep();
        })
      );
    }

    this.unpaidServices$ = this.store$.pipe(currentServices());
  }

  ngOnDestroy(): void {
    this.store$.dispatch(BookingActions.reset());
    unsubscribe(this.subscriptions);
  }

  setActive(next: Step): void {
    if (!next) {
      return;
    }
    if (next.id !== this.bookingService.currentStep.id) {
      this.bookingService.setStep({
        step: next,
      });
    }
  }

  navigateToStep(step: Breadcrumb): void {
    const bookingStep = BOOKING_STEPS_ARRAY.find((item) => item.id === step.id);

    this.bookingService.setStep({
      step: bookingStep,
    });
  }

  navigateBack(_event: Event): void {
    this.gtmService.trackElement(
      'booking-flow-hamburger-navi-back',
      GaContext.BOOKING_FLOW,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
    //go to previous step
    this.bookingService.prevStep();
  }

  toggleMenu(): void {
    this.navigationMenuService.toggleMenuOpen();
  }

  openBasket(): void {
    this.basketOpen$.next(true);
  }

  closeBasket(): void {
    this.basketOpen$.next(false);
  }

  edit(step: Step, openGlobalBookingWidget = false): void {
    this.basketOpen$.next(false);
    this.setActive(step);
    if (openGlobalBookingWidget) {
      this.bookingService.setGlobalBookingWidgetExpanderStatus(ExpanderStatus.OPEN);
    }
  }

  formatBreadcrumbs(
    currentStep: Step,
    breadcrumbs: Record<string, string>,
    activateLinks: boolean,
    hideAncillaries: boolean,
    hideSeatMap: boolean,
    enableNewStepper: boolean
  ): Breadcrumb[] {
    return BOOKING_STEPS_ARRAY.filter((step) => {
      const shouldHideBecauseSeatMap = hideSeatMap && step.id === BOOKING_STEPS.SEATS.id;
      const hideLastStep = step.id === BOOKING_STEPS.CHECKOUT.id;
      return (
        (!hideAncillaries ||
          step.id !== BOOKING_STEPS.ANCILLARIES.id ||
          step.id === BOOKING_STEPS.CHECKOUT.id ||
          hideLastStep) &&
        !shouldHideBecauseSeatMap
      );
    })
      .filter((step) => step.id !== BOOKING_STEPS.ENTRY.id)
      .map((step) => {
        if (step.id > BOOKING_STEPS.CHECKOUT?.id) {
          return;
        }

        const confirmationPageActive: boolean =
          currentStep.id === BOOKING_STEPS.PURCHASE_SUCCESS?.id ? step.id === BOOKING_STEPS.CHECKOUT?.id : false;
        const translationKey = enableNewStepper ? step.translationKeyV2 : step.translationKey;
        return {
          id: step.id,
          name: breadcrumbs[translationKey],
          active: step.id === currentStep.id || confirmationPageActive,
          disabled: !activateLinks || currentStep.id === BOOKING_STEPS.PURCHASE_SUCCESS?.id,
          icon: step.icon,
        };
      })
      .filter(Boolean);
  }

  onHomeClick(url: string): void {
    this.router.navigateByUrl(url);
  }

  private scrollToTopOnStepChange() {
    this.subscriptions.add(
      this.router.events.pipe(filter(isNavigationEvent)).subscribe(() => {
        this.scrollService.scrollTop();
      })
    );
  }

  private onNavigationSetBookingServiceStepFromUrl() {
    // Initial navigation
    this.setStepFromUrl(this.router.routerState.snapshot.url);

    const navigationEnd$: Observable<NavigationEnd> = this.router.events.pipe(
      filter<NavigationEnd>((e) => e instanceof NavigationEnd),
      finShare()
    );

    this.isErrorRoute$ = navigationEnd$.pipe(
      map((event) => event.url.includes(ERROR_STEPS.ERROR.path)),
      startWith(false)
    );

    // Successful navigation, e.g., back button or other router.navigate
    this.subscriptions.add(
      navigationEnd$.subscribe((event: NavigationEnd) => {
        this.setStepFromUrl(event.url);
      })
    );
  }

  private setStepFromUrl(url: string) {
    this.bookingService.setStep({
      step: getBookingStepFromUrl(url),
      skipRouteChange: true,
    });
  }

  private bindBasketHandling() {
    const hideModal$: Observable<boolean> = new Observable<number>((observer) => {
      return this.renderer.listen('window', 'resize', (event: Event) => observer.next(event.target['innerWidth']));
    }).pipe(
      debounceTime(100),
      filter((width) => width >= 600),
      mapTo(false)
    );

    this.subscriptions.add(hideModal$.subscribe(this.basketOpen$));
    this.subscriptions.add(
      this.basketOpen$
        .pipe(map((basketOpen) => this.navigationMenuService.modalOpenSubject$.next(basketOpen)))
        .subscribe()
    );
  }

  private initButtonVisibilities(): void {
    this.hasBasket$ = combineLatest([
      this.isFirstStep$,
      this.navigationMenuService.isNavigationMenuOpen$,
      this.currentStep$,
      this.bookingSummary$,
    ]).pipe(
      map(
        ([, isNavigationMenuOpen, currentStep, bookingSummary]) =>
          !isNavigationMenuOpen &&
          currentStep.id < BOOKING_STEPS.PURCHASE_REVIEW.id &&
          !(currentStep.id === BOOKING_STEPS.FLIGHT_SELECTION.id && bookingSummary.tripType !== TripType.MULTICITY)
      ),
      finShare(),
      distinctUntilChanged()
    );

    this.enableModifySearch$ = combineLatest([this.hasBasket$, this.currentStep$]).pipe(
      map(([hasBasket, currentStep]) => !hasBasket && currentStep.id === BOOKING_STEPS.FLIGHT_SELECTION.id)
    );
  }
}
